/**
 * Utilisateurs de la plateforme — l'équipe d'INFRATEX.
 *
 * **On ne crée pas ici les utilisateurs des clients.** Ils les créent eux-mêmes,
 * depuis leur espace, comme ils l'ont fait à l'inscription sur la plateforme de
 * test. Ce que l'exploitant ouvre chez un client, c'est le compte et son premier
 * propriétaire — écran « Comptes clients » ; la suite appartient au client.
 *
 * Cet écran sert donc à deux choses, et pas une de plus :
 *
 *   1. créer et administrer les personnes d'INFRATEX qui gèrent la plateforme ;
 *   2. **lire** l'équipe d'un compte client, pour le support — « qui peut créer
 *      une clé chez ce client » est la première question quand une clé fuit —
 *      avec deux gestes de dépannage qu'un client bloqué ne peut pas faire
 *      lui-même.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  api,
  type CompteAdmin,
  type ContexteCompte,
  type DescriptionRole,
  type RoleClient,
  type UtilisateurAdmin,
} from '../../api/client';
import {
  Bandeau,
  Carte,
  Champ,
  Chargement,
  EtatVide,
  Grille,
  MessageErreur,
  MessageSucces,
  PuceLibre,
} from '../../components/Ui';
import { formaterDate } from '../../lib/format';

export const ROLES: RoleClient[] = ['owner', 'admin', 'member', 'developer'];

export const LIBELLE_ROLE: Record<RoleClient, string> = {
  owner: 'Propriétaire',
  admin: 'Administrateur',
  member: 'Utilisateur',
  developer: 'Développeur',
};

/** Ce que chaque rôle sert à faire, en une phrase. Le détail vient du serveur. */
export const USAGE_ROLE: Record<RoleClient, string> = {
  owner: 'Responsable du compte. Seul à pouvoir modifier les mentions légales.',
  admin: 'Administre l’usage quotidien et l’équipe, sans engager la société.',
  member: 'Campagnes, contacts, modèles. Ne détient aucune clé API.',
  developer: 'Intégration : clés, webhooks, journaux. Ne voit pas la facturation.',
};

// --- Écran principal ---------------------------------------------------------

export function Utilisateurs() {
  const [roles, setRoles] = useState<DescriptionRole[]>([]);
  const [erreur, setErreur] = useState<unknown>(null);

  useEffect(() => {
    api.admin
      .roles()
      .then(({ data }) => setRoles(data))
      .catch(setErreur);
  }, []);

  if (erreur) return <MessageErreur erreur={erreur} />;

  return (
    <>
      <EquipeDeLaPlateforme roles={roles} />
      <EquipeDunClient />
      <CatalogueDesRoles roles={roles} />
    </>
  );
}

// --- L'équipe de l'exploitant -------------------------------------------------

function EquipeDeLaPlateforme({ roles }: { roles: DescriptionRole[] }) {
  const [equipe, setEquipe] = useState<UtilisateurAdmin[] | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [ajout, setAjout] = useState(false);

  const charger = useCallback(() => {
    api.admin
      .utilisateursPlateforme()
      .then(({ data }) => setEquipe(data))
      .catch(setErreur);
  }, []);

  useEffect(charger, [charger]);

  async function modifier(id: string, champs: Parameters<typeof api.admin.modifierUtilisateur>[1]) {
    setErreur(null);
    setSucces(null);
    try {
      const { user, revoked_sessions } = await api.admin.modifierUtilisateur(id, champs);
      setSucces(messageDeModification(user.email, revoked_sessions));
      charger();
    } catch (e) {
      setErreur(e);
    }
  }

  if (erreur && !equipe) return <MessageErreur erreur={erreur} />;
  if (!equipe) return <Chargement quoi="Chargement de l’équipe…" />;

  return (
    <Carte
      titre="Équipe de la plateforme"
      action={
        <button className="secondaire" onClick={() => setAjout((v) => !v)}>
          {ajout ? 'Fermer' : 'Créer un utilisateur'}
        </button>
      }
    >
      <Bandeau ton="orange">
        Ces personnes administrent <strong>toute la plateforme</strong> : comptes clients,
        registres, facturation, conformité. Elles appartiennent au compte d’INFRATEX, et la base
        refuse qu’un utilisateur d’un compte client obtienne ces droits.
      </Bandeau>

      {erreur ? <MessageErreur erreur={erreur} /> : null}
      {succes && <MessageSucces>{succes}</MessageSucces>}

      {ajout && (
        <FormulaireUtilisateur
          roles={roles}
          onCree={(message) => {
            setAjout(false);
            setErreur(null);
            setSucces(message);
            charger();
          }}
        />
      )}

      <TableauEquipe
        equipe={equipe}
        onRole={(id, role) => modifier(id, { role })}
        onActivation={(id, actif) => modifier(id, { is_active: actif })}
      />

      <div className="aide">
        Toute modification de droits <strong>ferme les sessions ouvertes</strong> de la personne :
        le rôle est figé dans la session à la connexion, et sans cela un droit retiré resterait
        actif jusqu’à douze heures.
      </div>
    </Carte>
  );
}

function FormulaireUtilisateur({
  roles,
  onCree,
}: {
  roles: DescriptionRole[];
  onCree: (message: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [nom, setNom] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [role, setRole] = useState<RoleClient>('admin');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);

  const description = roles.find((r) => r.role === role);

  async function soumettre() {
    setEnvoi(true);
    setErreur(null);
    try {
      const { user } = await api.admin.creerUtilisateurPlateforme({
        email,
        password: motDePasse,
        full_name: nom || undefined,
        role,
      });
      onCree(
        `${user.email} rejoint l’équipe de la plateforme avec le rôle « ${LIBELLE_ROLE[user.role]} ». ` +
          'Transmettez le mot de passe par un canal distinct de la console.',
      );
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
      {erreur ? <MessageErreur erreur={erreur} /> : null}

      <Grille>
        <Champ libelle="Nom" valeur={nom} onChange={setNom} />
        <Champ libelle="Adresse e-mail" type="email" valeur={email} onChange={setEmail} requis />
        <Champ
          libelle="Mot de passe provisoire"
          type="password"
          valeur={motDePasse}
          onChange={setMotDePasse}
          aide="10 caractères minimum."
          requis
        />
        <div className="champ">
          <label htmlFor="role-plateforme">Rôle sur le compte INFRATEX</label>
          <select
            id="role-plateforme"
            value={role}
            onChange={(e) => setRole(e.target.value as RoleClient)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {LIBELLE_ROLE[r]}
              </option>
            ))}
          </select>
          <div className="aide" style={{ marginTop: 4 }}>
            {USAGE_ROLE[role]}
          </div>
        </div>
      </Grille>

      {description && (
        <div className="aide" style={{ marginTop: 0 }}>
          <strong>Ce rôle donne, sur le compte INFRATEX :</strong>{' '}
          {description.permissions.map((p) => p.label).join(' · ')}. <strong>S’y ajoutent</strong>{' '}
          les droits d’administration de la plateforme, que porte tout utilisateur créé ici.
        </div>
      )}

      <div>
        <button onClick={soumettre} disabled={envoi || !email || motDePasse.length < 10}>
          {envoi ? 'Création…' : 'Créer l’utilisateur'}
        </button>
      </div>
    </div>
  );
}

// --- L'équipe d'un client, en lecture ----------------------------------------

function EquipeDunClient() {
  const [comptes, setComptes] = useState<CompteAdmin[] | null>(null);
  const [choisi, setChoisi] = useState<string | null>(null);
  const [equipe, setEquipe] = useState<UtilisateurAdmin[] | null>(null);
  const [contexte, setContexte] = useState<ContexteCompte | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [succes, setSucces] = useState<string | null>(null);

  useEffect(() => {
    api.admin
      .comptes()
      .then(({ data }) => {
        setComptes(data);
        setChoisi((actuel) => actuel ?? data[0]?.id ?? null);
      })
      .catch(setErreur);
  }, []);

  const charger = useCallback(() => {
    if (!choisi) return;
    setEquipe(null);
    api.admin
      .utilisateurs(choisi)
      .then(({ data, context }) => {
        setEquipe(data);
        setContexte(context);
      })
      .catch(setErreur);
  }, [choisi]);

  useEffect(charger, [charger]);

  async function depanner(id: string, champs: Parameters<typeof api.admin.modifierUtilisateur>[1]) {
    setErreur(null);
    setSucces(null);
    try {
      const { user, revoked_sessions } = await api.admin.modifierUtilisateur(id, champs);
      setSucces(messageDeModification(user.email, revoked_sessions));
      charger();
    } catch (e) {
      setErreur(e);
    }
  }

  if (erreur && !comptes) return <MessageErreur erreur={erreur} />;
  if (!comptes) return <Chargement quoi="Chargement des comptes…" />;

  return (
    <Carte titre="Équipe d’un compte client">
      <div className="aide" style={{ marginTop: 0 }}>
        En lecture. <strong>Le client crée ses propres utilisateurs</strong> depuis son espace —
        vous n’en ajoutez pas à sa place. Restent ici les deux gestes qu’un client bloqué ne peut
        pas faire lui-même : désactiver une fiche compromise, réinitialiser un mot de passe perdu.
        Les deux sont journalisés.
      </div>

      {comptes.length === 0 ? (
        <EtatVide titre="Aucun compte client">
          Ouvrez le premier compte depuis l’écran « Comptes clients ».
        </EtatVide>
      ) : (
        <div className="champ" style={{ marginTop: 12 }}>
          <label htmlFor="compte-consulte">Compte</label>
          <select
            id="compte-consulte"
            value={choisi ?? ''}
            onChange={(e) => setChoisi(e.target.value)}
          >
            {comptes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.sandbox_only ? ' — intégration' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {erreur ? <MessageErreur erreur={erreur} /> : null}
      {succes && <MessageSucces>{succes}</MessageSucces>}

      {contexte && (contexte.proprietaires_actifs ?? 0) <= 1 && (
        <div className="aide">
          Ce compte n’a qu’un seul propriétaire actif. Son rôle ne peut pas être retiré tant qu’un
          autre n’a pas pris le relais — sinon plus personne n’y gérerait les{' '}
          {contexte.cles_actives} clé(s) API active(s).
        </div>
      )}

      {equipe ? (
        <TableauEquipe
          equipe={equipe}
          lectureSeule
          onActivation={(id, actif) => depanner(id, { is_active: actif })}
        />
      ) : (
        <Chargement quoi="Chargement de l’équipe…" />
      )}
    </Carte>
  );
}

// --- Tableau commun -----------------------------------------------------------

function TableauEquipe({
  equipe,
  onRole,
  onActivation,
  lectureSeule,
}: {
  equipe: UtilisateurAdmin[];
  onRole?: (id: string, role: RoleClient) => void;
  onActivation: (id: string, actif: boolean) => void;
  lectureSeule?: boolean;
}) {
  if (equipe.length === 0) {
    return <EtatVide titre="Personne pour l’instant" />;
  }

  return (
    <div className="tableau-conteneur" style={{ marginTop: 12 }}>
      <table style={{ minWidth: 820 }}>
        <thead>
          <tr>
            <th>Personne</th>
            <th>Rôle</th>
            <th>Droits</th>
            <th>Dernière connexion</th>
            <th>État</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {equipe.map((u) => (
            <tr key={u.id}>
              <td>
                <div style={{ fontWeight: 500 }}>{u.full_name ?? u.email}</div>
                <div style={{ color: 'var(--muet)', fontSize: 12 }}>{u.email}</div>
                {u.is_platform_admin && <PuceLibre ton="attente">Plateforme</PuceLibre>}
              </td>
              <td>
                {lectureSeule || !onRole ? (
                  LIBELLE_ROLE[u.role]
                ) : (
                  <select
                    value={u.role}
                    onChange={(e) => onRole(u.id, e.target.value as RoleClient)}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {LIBELLE_ROLE[r]}
                      </option>
                    ))}
                  </select>
                )}
              </td>
              <td style={{ maxWidth: 260 }}>
                <div style={{ color: 'var(--muet)', fontSize: 12 }}>
                  {u.permissions.length} droit(s) — {USAGE_ROLE[u.role]}
                </div>
              </td>
              <td style={{ fontSize: 12 }}>
                {u.last_login_at ? formaterDate(u.last_login_at) : 'Jamais connecté'}
              </td>
              <td>
                {u.is_active ? (
                  <PuceLibre ton="succes">Actif</PuceLibre>
                ) : (
                  <PuceLibre ton="echec">Désactivé</PuceLibre>
                )}
              </td>
              <td>
                <button className="secondaire" onClick={() => onActivation(u.id, !u.is_active)}>
                  {u.is_active ? 'Désactiver' : 'Réactiver'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function messageDeModification(email: string, sessionsFermees: number): string {
  return sessionsFermees > 0
    ? `${email} : droits mis à jour, ${sessionsFermees} session(s) fermée(s). La personne devra se reconnecter.`
    : `${email} : droits mis à jour.`;
}

// --- Le catalogue ------------------------------------------------------------

export function CatalogueDesRoles({ roles }: { roles: DescriptionRole[] }) {
  if (roles.length === 0) return null;

  return (
    <Carte titre="Ce que porte chaque rôle">
      <div className="tableau-conteneur">
        <table style={{ minWidth: 760 }}>
          <thead>
            <tr>
              <th>Rôle</th>
              <th>Usage</th>
              <th>Droits</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.role}>
                <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{r.label}</td>
                <td style={{ fontSize: 12, color: 'var(--muet)' }}>{USAGE_ROLE[r.role]}</td>
                <td style={{ fontSize: 12 }}>{r.permissions.map((p) => p.label).join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="aide">
        Cette table vient du serveur : c’est la même liste qui décide des refus. Un rôle modifié
        dans le code se voit ici sans qu’on ait à retoucher cet écran.
      </div>
    </Carte>
  );
}
